import React, { ReactNode, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TableChartIcon from "@mui/icons-material/TableChart";
import { ExcelImportProcess, ExcelOrigin } from "types/process";
import {
  getElapsedMsFromProcess,
  formatDuration,
  getElapsedLabel,
} from "utils/process.util";
import { columnNumberToExcelLetter } from "utils/excel.util";
import { exportImportResultsToExcel } from "./ImportFromExcelResultsExport";
import { ShowValidationsButton } from "../ShowValidationsButton";

interface ImportFromExcelResultSectionProps {
  process: ExcelImportProcess;
}

type ExpandKey = "created" | "replaced" | "skipped";

export default function ImportFromExcelResultSection({
  process,
}: ImportFromExcelResultSectionProps) {
  const [expanded, setExpanded] = useState<Record<ExpandKey, boolean>>({
    created: false,
    replaced: false,
    skipped: false,
  });
  const [showSkippedIds, setShowSkippedIds] = useState(false);

  const toggleRow = (key: ExpandKey) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const elapsedMs = getElapsedMsFromProcess(process);
  const elapsedFormatted = formatDuration(elapsedMs);
  const elapsedLabel = getElapsedLabel();

  interface SummaryRow {
    key?: ExpandKey;
    label: string;
    value?: number;
    indent?: boolean;
    muted?: boolean;
    warning?: boolean;
  }

  const propertyRows: SummaryRow[] = [
    { label: "Read", value: process.parsedPropertyCount },
    { label: "Imported", value: process.importedPropertyCount },
    {
      key: "created",
      label: "Created",
      value: process.createdPropertyCount,
      indent: true,
      muted: true,
    },
    {
      key: "replaced",
      label: "Replaced",
      value: process.replacedPropertyCount,
      indent: true,
      muted: true,
    },
    {
      key: "skipped",
      label: "Skipped",
      value: process.skippedPropertyCount,
      warning: process.skippedPropertyCount > 0,
    },
  ];

  const renderNameList = (title: string, names: string[]): ReactNode => {
    if (names.length === 0) return null;

    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Box sx={{ maxHeight: 180, overflowY: "auto", pl: 1 }}>
          {names.map((name) => (
            <Typography key={name} variant="body2">
              • {name}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  const renderSkippedTable = (): ReactNode => {
    if (!process.skippedProperties?.length) return null;

    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          mb={1}
          spacing={3}
        >
          <Typography variant="subtitle2" fontWeight={600} color="warning.main">
            Skipped properties
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showSkippedIds}
                onChange={(e) => setShowSkippedIds(e.target.checked)}
              />
            }
            label="Show IDs"
          />
        </Stack>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 260 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {showSkippedIds && (
                  <TableCell
                    sx={{
                      width: 170,
                      minWidth: 170,
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                    }}
                  >
                    ID
                  </TableCell>
                )}

                <TableCell>Location</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {process.skippedProperties.map((p) => (
                <TableRow key={p.id}>
                  {/* Skipped Property Name */}
                  <TableCell>{p.name}</TableCell>

                  {/* ID: optionally shown  */}
                  {showSkippedIds && (
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        maxWidth: 170,
                        wordBreak: "break-all",
                      }}
                    >
                      {p.id}
                    </TableCell>
                  )}

                  {/* LOCATION */}
                  <TableCell>
                    <Stack spacing={0.25}>
                      {formatExcelOrigin(p.excelOrigin, true).map(
                        (line, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {line}
                          </Typography>
                        )
                      )}
                    </Stack>
                  </TableCell>

                  {/* REASON */}
                  <TableCell>{p.reason}</TableCell>

                  {/* ERRORS */}
                  <TableCell>
                    <Stack spacing={0.5}>
                      {p.errors.map((err, index) => (
                        <Typography key={index} variant="body2">
                          {err}
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const ExpandIconSlot = ({
    visible,
    expanded,
  }: {
    visible: boolean;
    expanded: boolean;
  }) => (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        width: 20,
        justifyContent: "center",
        visibility: visible ? "visible" : "hidden",
        mr: 1,
        color: "text.secondary",
        flexShrink: 0,
      }}
    >
      {expanded ? (
        <ExpandMoreIcon fontSize="small" />
      ) : (
        <ChevronRightIcon fontSize="small" />
      )}
    </Box>
  );

  function formatExcelOrigin(
    origin: ExcelOrigin,
    useExcelColumnLetters: boolean
  ): string[] {
    const lines: string[] = [];

    // Comment this out until we are importing multiple workbooks.
    // if (origin.workbook?.trim()) {
    //   lines.push(`workbook: '${origin.workbook}'`);
    // }

    if (origin.worksheet?.trim()) {
      lines.push(`worksheet: '${origin.worksheet}'`);
    }

    let rowLine = `row: ${origin.row}`;

    if (origin.column !== null) {
      const columnValue = useExcelColumnLetters
        ? columnNumberToExcelLetter(origin.column)
        : origin.column;

      rowLine += `, column: ${columnValue}`;
    }

    lines.push(rowLine);

    return lines;
  }

  const renderSummaryTable = (title: string, rows: SummaryRow[]): ReactNode => (
    <Stack spacing={1}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            {rows.map((row) => {
              const expandableKey = row.key;
              const expandable =
                Boolean(expandableKey) &&
                row.value !== undefined &&
                row.value > 0;

              const isExpanded = expandableKey
                ? expanded[expandableKey]
                : false;

              const onClickHandler =
                expandableKey && expandable
                  ? () => toggleRow(expandableKey)
                  : undefined;

              return (
                <React.Fragment key={row.label}>
                  <TableRow
                    hover={expandable}
                    onClick={onClickHandler}
                    sx={{
                      cursor: expandable ? "pointer" : "default",
                    }}
                  >
                    <TableCell
                      sx={{
                        pl: 1.5,
                        fontWeight: row.indent ? 400 : 600,
                        color: row.warning
                          ? "warning.main"
                          : row.muted
                          ? "text.secondary"
                          : "text.primary",
                      }}
                    >
                      <Stack direction="row" alignItems="center">
                        {/* Expand icon column (always aligned) */}
                        <ExpandIconSlot
                          visible={expandable}
                          expanded={isExpanded}
                        />

                        {/* Indent spacer (only for child rows) */}
                        {row.indent && (
                          <Box
                            sx={{
                              width: 20,
                              flexShrink: 0,
                            }}
                          />
                        )}

                        {/* Label */}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: "inherit" }}
                        >
                          {row.label}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: row.warning ? "warning.main" : "text.primary",
                      }}
                    >
                      {row.value ?? "—"}
                    </TableCell>
                  </TableRow>

                  {isExpanded && row.key === "created" && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        {renderNameList(
                          "Created properties",
                          process.createdProperties
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {isExpanded && row.key === "replaced" && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        {renderNameList(
                          "Replaced properties",
                          process.replacedProperties
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {isExpanded && row.key === "skipped" && (
                    <TableRow>
                      <TableCell colSpan={2}>{renderSkippedTable()}</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  const ValidationIssuesSection = ({
    process,
  }: {
    process: ExcelImportProcess;
  }) => {
    // Validation counts
    const plValidations = process.productlineNumValidations ?? 0;
    const propValidations = process.propertyNumValidations ?? 0;
    const tableValidations = process.tableNumValidations ?? 0;

    return (
      <Box mt={3}>
        {/* Title */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Validation Issues Found
        </Typography>

        {/* Validation counts */}
        <Grid container spacing={1} mb={1}>
          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Product line validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{ color: plValidations > 0 ? "error.main" : "text.primary" }}
            >
              {plValidations}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Property validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{
                color: propValidations > 0 ? "error.main" : "text.primary",
              }}
            >
              {propValidations}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Table validations
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography
              variant="body2"
              textAlign="right"
              sx={{
                color: tableValidations > 0 ? "error.main" : "text.primary",
              }}
            >
              {tableValidations}
            </Typography>
          </Grid>
        </Grid>

        {/* Button */}
        <ShowValidationsButton
          label="Show All Validation Issues"
          fullWidth
          tooltipPlacement="bottom"
        />
      </Box>
    );
  };

  return (
    <Stack spacing={2}>
      <Tooltip
        title="Export import results to Excel (Created, Replaced, and Skipped properties)"
        arrow
      >
        <span>
          <Button
            size="medium"
            variant="outlined"
            fullWidth
            startIcon={<TableChartIcon />}
            onClick={() => exportImportResultsToExcel(process)}
            disabled={
              process.parsedPropertyCount + process.parsedTableCount === 0
            }
          >
            Export Results
          </Button>
        </span>
      </Tooltip>

      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Import summary
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              Worksheets Imported
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography variant="body2" textAlign="right">
              {process.completedWorksheets} / {process.totalWorksheets}
            </Typography>
          </Grid>

          <Grid item xs={8} sm={7} md={6}>
            <Typography variant="body2" fontWeight={600}>
              {elapsedLabel}
            </Typography>
          </Grid>
          <Grid item xs={4} sm={5} md={6}>
            <Typography variant="body2" textAlign="right">
              {elapsedFormatted}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {renderSummaryTable("Property Import Results", propertyRows)}

      <Divider />

      <ValidationIssuesSection process={process} />
    </Stack>
  );
}
